import React from 'react'
import { isNil, noop } from 'lodash-es'
import { ValidationIssue } from 'api'
import { Field, TextInput, Focusable } from '../inputs'
import { MenuContainer } from '../menus'
import { Listbox, ListboxOptions, OptionKey, OptionTemplateProps } from './Listbox'
import { iconSet } from '../icons'
import { DropdownListbox, SelectboxPrimary, SelectboxInput, DropdownButton, SelectboxOptionTemplate } from './optionsStyles'

export interface SelectboxProps<T, K extends OptionKey> extends ListboxOptions<T, K> {
  id?: string
  className?: string
  readOnly?: boolean
  disabled?: boolean
  withPortal?: boolean
  validationIssues?: ValidationIssue[]
  placeholder?: string
  canClear?: boolean
  value: K | null | undefined
  dataTestId?: string
  onUpdate: (value: K | null, ...rest: any[]) => void
  onPaste?: (evt: React.ClipboardEvent) => unknown
  hideInput?: boolean
  updateOnlyOnChangedValue?: boolean
  children?: React.ReactNode
}

interface SelectboxState {
  isDroppedDown: boolean
}

export class Selectbox extends React.PureComponent<SelectboxProps<any, OptionKey>, SelectboxState> implements Focusable {
  constructor(props: SelectboxProps<any, OptionKey>) {
    super(props)
    this.state = { isDroppedDown: false }
  }

  private listboxRef = React.createRef<Listbox>()
  private inputRef = React.createRef<HTMLInputElement>()

  public setFocus = () => {
    if (this.inputRef.current) {
      this.inputRef.current.select()
    }
  }

  private getSelectedOption(): any | null {
    const { getOption, value } = this.props

    if (isNil(value)) {
      return null
    } else if (getOption) {
      return getOption(value)
    } else {
      return value
    }
  }

  private getSelectedOptionText(): string {
    const { getOptionText, nullItemText } = this.props
    const selectedOption = this.getSelectedOption()

    if (isNil(selectedOption)) {
      return nullItemText || ''
    } else if (getOptionText) {
      return getOptionText(selectedOption)
    } else {
      return selectedOption
    }
  }

  private handleUpdate = (value: OptionKey) => {
    if (this.props.updateOnlyOnChangedValue && value === this.props.value) {
      return
    }

    this.props.onUpdate(value)
  }

  private handleKeyDown = (evt: React.KeyboardEvent<HTMLElement>) => {
    if (this.state.isDroppedDown && this.listboxRef.current) {
      this.listboxRef.current.handleKeyDown(evt)
    }
  }

  private handleUpdateMenuVisible = (menuVisible: boolean) => {
    this.setState({ isDroppedDown: menuVisible })
  }

  private handleFocus = (_ev: React.FocusEvent<HTMLElement>) => {
    this.setFocus()
  }

  public render() {
    const { readOnly, disabled } = this.props
    return readOnly && !disabled ? this.renderReadOnly() : this.renderEditable()
  }

  private renderReadOnly() {
    const selectedOptionText = this.getSelectedOptionText()

    return (
      <TextInput
        id={this.props.id}
        className={this.props.className}
        value={selectedOptionText}
        readOnly={this.props.readOnly}
        onUpdate={noop}
        validationIssues={this.props.validationIssues}
      />
    )
  }

  private renderOption = (optionProps: OptionTemplateProps<any, OptionKey>) => {
    return <SelectboxOptionTemplate {...optionProps}>{optionProps.optionText}</SelectboxOptionTemplate>
  }

  private renderInput = () => {
    if (this.props.hideInput) {
      return null
    }
    if (React.Children.count(this.props.children) > 0) {
      return this.props.children
    } else {
      const selectedOptionText = this.getSelectedOptionText()

      return (
        <SelectboxInput
          data-test-id={this.props.dataTestId}
          ref={this.inputRef}
          id={this.props.id}
          placeholder={this.props.placeholder}
          type="text"
          autoComplete="off"
          spellCheck={false}
          value={selectedOptionText}
          readOnly={true}
          onFocus={this.handleFocus}
          onPaste={this.props.onPaste}
        />
      )
    }
  }

  private renderEditable(): JSX.Element {
    const state = this.state
    const { id, className, value, canClear, disabled, validationIssues, optionKeys, withPortal, ...rest } = this.props
    const allOptionKeys = canClear ? [null, ...optionKeys] : optionKeys

    const primaryComponent = (
      <SelectboxPrimary disabled={disabled ? true : undefined} readOnly={false} validationIssues={validationIssues}>
        {this.renderInput()}
        <DropdownButton
          onClick={this.setFocus}
          canFocus={false}
          disabled={disabled}
          readOnly={false}
          validationIssues={validationIssues}
          dataTestId={`${this.props.dataTestId}-DropdownButton`}>
          {state.isDroppedDown ? iconSet.chevronUp : iconSet.chevronDown}
        </DropdownButton>
      </SelectboxPrimary>
    )

    return (
      <MenuContainer
        className={className}
        withPortal={withPortal}
        disabled={disabled}
        primaryComponent={primaryComponent}
        menuVisible={this.state.isDroppedDown}
        onUpdateMenuVisible={this.handleUpdateMenuVisible}
        onKeyDown={this.handleKeyDown}>
        <DropdownListbox
          optionTemplate={this.renderOption}
          {...rest}
          value={value}
          ref={this.listboxRef}
          enableHighlight={true}
          optionKeys={allOptionKeys}
          onUpdate={this.handleUpdate}
        />
      </MenuContainer>
    )
  }
}

export const SelectboxField = Field(Selectbox)
